// =============================================================================
// How a batch of posts is actually doing.
//
// A campaign and a monthly plan each carry their own `status`, set when the
// batch was generated and sent for approval. That status does not move when
// the posts inside it do, so a plan whose posts have all been approved went on
// reading "waiting for approval" — which is not a stale label, it is the
// screen telling the owner something untrue about work they just finished.
//
// Rather than keeping a rolled-up column in step with every approval, skip and
// publish, the breakdown is derived from the posts whenever it is shown. A
// derived number cannot go stale.
// =============================================================================
/**
 * `draft` counts as waiting, matching the approvals queue.
 *
 * A draft is a post nobody has decided about yet, and that is the question
 * this summary answers. The draft/pending distinction matters to the WhatsApp
 * sender, not to someone looking at how far through a month they are.
 */
export function summarisePosts(statuses) {
    const progress = {
        total: statuses.length,
        waiting: 0,
        approved: 0,
        published: 0,
        skipped: 0,
        failed: 0,
        settled: true,
    };
    for (const status of statuses) {
        switch (status) {
            case "draft":
            case "pending_approval":
                progress.waiting += 1;
                break;
            case "approved":
            case "scheduled":
                progress.approved += 1;
                break;
            case "published":
                progress.published += 1;
                break;
            case "skipped":
                progress.skipped += 1;
                break;
            case "failed":
            case "partially_failed":
                progress.failed += 1;
                break;
            default:
                // An unrecognised status is treated as undecided rather than ignored:
                // a post missing from every count would make the totals lie.
                progress.waiting += 1;
        }
    }
    progress.settled = progress.waiting === 0;
    return progress;
}
/**
 * The single word for a batch, when there is only room for one.
 *
 * Ordered by what the owner has to act on: anything still waiting dominates,
 * then a failure they should look at, then the happy states.
 */
export function headlineStatus(progress, storedStatus) {
    // These say something the posts cannot: a cancelled batch may still hold
    // published posts, and a generating one has not written them yet.
    if (storedStatus === "cancelled" || storedStatus === "generating" || storedStatus === "draft") {
        return storedStatus;
    }
    if (progress.total === 0)
        return "empty";
    if (progress.waiting > 0)
        return "waiting";
    if (progress.failed > 0)
        return "failed";
    if (progress.published === progress.total)
        return "published";
    return "approved";
}
//# sourceMappingURL=postProgress.js.map